import { ChangeDetectionStrategy, Component, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Observable, combineLatest, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { CovanceGridColumn } from '../../../covance-ag-grid';
import { StudyGroupInfo, SampleTrackerData } from '../../group-info/study-group-info';
import { SampleStatusInfo } from './sample-status/sample-status-info';
import { SampleTrackingStorage } from '../../sample-tracking-storage';
import { ProtocolFilterData, PortfolioFilterRequestData, SampleCountData, FilterTypeEnum, SampleTrackingSampleCountRequest, SelectedFilter, FilterNames, ProtocolSponsorPairs } from './dashboard-info';
import { IFilterSelectItem } from './../../shared/sample-tracking-select/sample-tracking-select.component';
import { SampleTrackingSelectFilterService } from './../../shared/sample-tracking-select/sample-tracking-select-filter-service';
import { SampleStatusService } from './sample-status/sample-status-service';
import { UrlBuilder } from '../../../core';
import { Pager } from '../../../shared/covance-grid/pagination/pagination.component';
import { FilterSamplesInfo } from '../../filter-samples/filter-samples-info';
import { ActivityLogger } from '../../../activities/activity-logger';
import { IBreadcrumb, BreadcrumbService } from '../../shared/breadcrumb-service/breadcrumb.service';
import { FiltersStorage, UserFilter } from '../../../covance-filter/filters-storage';
import { SamplesHelperService } from '../../filter-samples/service/samples-helper-service';
import { FilterLabels, ExcludeFilterItems } from '../../reports/interfaces/filter-data';
import { SubjectSearchRequest } from '../../tracking-overview/interfaces/subject-search-data';
import { request } from 'd3';
import { GlobalErrorHandler } from '../../../core/global-error-handler';
import { MdlSnackbarComponent } from '@angular-mdl/core';

@Component({
    selector: 'overview-dashboard',
    template: require('./overview-dashboard.component.html'),
    styles: [require('./overview-dashboard.component.scss')],
    providers: [SampleTrackingSelectFilterService, SampleStatusService],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OverviewDashboardComponent implements OnDestroy {

    public columnDefs$: CovanceGridColumn[];
    public dataList: ProtocolFilterData[] = [];
    public selectedProtocol$: SampleTrackerData;
    public selectedStudyGroup$: StudyGroupInfo;
    public isProtocol = false;
    public loadingComplete = false;
    public defaultProtocol: IFilterSelectItem;
    public pager: Pager;
    public allCountries$: IFilterSelectItem[];
    public projects$: IFilterSelectItem[];
    public regions$: IFilterSelectItem[];
    public invNumber$: IFilterSelectItem[];
    public protocol: IFilterSelectItem[];
    public protocolFilters: IFilterSelectItem[];
    public selectedFilterList: SelectedFilter[] = [];
    public sampleStatusList: SampleStatusInfo[];
    public selectedStatus: IFilterSelectItem;
    public currentFilter: FilterTypeEnum = FilterTypeEnum.protocol;
    public previousRequestData: SampleTrackingSampleCountRequest;
    public showMyFilterLoading = false;
    public isEnableSaveFilter = false;
    public defaultProtocolFilter: IFilterSelectItem[] = [];
    public defaultProjectFilter: IFilterSelectItem[] = [];
    public defaultRegionFilter: IFilterSelectItem[] = [];
    public defaultCountryFilter: IFilterSelectItem[] = [];
    public defaultInvestigatorFilter: IFilterSelectItem[] = [];
    public myFilterItems: UserFilter[];
    public filterInfo: FilterLabels = {
        title: 'My Filter',
        newText: 'New Filter',
        editText: 'Edit Filter',
        deleteText: 'Delete Filter',
        infoMsg: 'Select from your Saved Filters or type to add new filter name',
        menuToolTip: 'Create, edit or delete filters',
        groupName: 'overview-dashboard'
    };
    private subscription: Subscription;
    private manualDrawer = false;
    private collapseDrawer = false;
    private selectedFilter: UserFilter;
    private isLoadCountEnabled = true;
    private protocolSponsorPairs: ProtocolSponsorPairs[] = [];
    private filterCount = 0;

    constructor(
        private storage: SampleTrackingStorage,
        private activatedRoute: ActivatedRoute,
        private selectFilterService: SampleTrackingSelectFilterService,
        private samplestatusService: SampleStatusService,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly urlBuilder: UrlBuilder,
        private breadCrumbService: BreadcrumbService,
        activityLogger: ActivityLogger,
        private samplesService: SamplesHelperService,
        private filterStorage: FiltersStorage,
    ) {
        let dashboardData = localStorage.getItem('dashboardData');
        if (!dashboardData) {
            this.urlBuilder.esa().sampletracking().home().navigate();
            return;
        } 
        this.samplesService.alignBreadcrumb('66px');
        this.initializePageData(dashboardData);
        this.protocol = this.loadProtocolData();
        this.subscription = this.getProtocolFilterData().
            pipe(
                map((sites: ProtocolFilterData[]) => {
                    if (!sites || sites.length === 0) {
                        this.samplesService.showNoDataText();
                    }
                    let data = this.setFilterData(sites);
                    this.protocolSponsorPairs = this.samplesService.getProtocolFilterPairs(data);
                    this.loadSampleCountData();
                    return data;
                })).
            subscribe((filterData: ProtocolFilterData[]) => {
                this.dataList = filterData;
                this.changeDetectorRef.markForCheck();
            });
        this.myFilterItems = this.filterStorage.getFilters(this.filterInfo.groupName, null, null);
        activityLogger.logActivity('Access Sample Tracking Dashboard');
    }

    public ngOnDestroy() {
        if (!this.subscription) { return; }
        this.subscription.unsubscribe();
        this.samplesService.alignBreadcrumb('unset');
    }

    public get isDrawerCollapsed(): boolean {
        return this.collapseDrawer;
    }

    public isLoading(filterList: Observable<string[]>) {
        const loading = combineLatest([filterList])
            .pipe(map((value) => { return value && value.length > 0; }));
        return loading;
    }

    public applyFilter() {
        const filterInfo = this.setListViewData();
        if (this.isProtocol) {
            this.urlBuilder.esa().sampletracking().protocolInfo(this.selectedProtocol$).filterSamples(this.selectedStatus.key).navigate();
            return;
        }
        this.urlBuilder.esa().sampletracking().studyGroup(this.selectedStudyGroup$).filterSamples(this.selectedStatus.key).navigate();
        return;
    }

    public onStatusSelectChange($event: SampleStatusInfo) {
        this.selectedStatus = $event.filterItem;
        this.applyFilter();
    }

    public onSubjectSearch($event: string) {
        if (!$event || $event.length === 0) { return; }
        let data: SubjectSearchRequest = {
            patientNumber: $event,
            sponsorProtocolPair: this.protocol.map(pt => {
                return {
                    protocolCode: pt.value,
                    sponsorName: pt.sponsorName
                };
            })
        };
        localStorage.setItem('subjectSearchRequest', JSON.stringify(data));
        this.setListViewData();
        this.urlBuilder.esa().sampletracking().subjectSearchResults($event).navigate();
    }

    public clearFilter(loadCount = true) {
        this.isEnableSaveFilter = false;
        this.selectedFilterList = [];
        this.selectedStatus = null;
        this.defaultProtocolFilter = null;
        this.defaultRegionFilter = null;
        this.defaultProjectFilter = null;
        this.defaultCountryFilter = null;
        this.defaultInvestigatorFilter = null;
        this.selectFilterService.clearFilterSelection(true);
        if (loadCount) {
            this.selectFilterService.resetMyFilters.next(true);
        }
        this.setFilterData(this.dataList, {});
        this.loadSampleCountData();
    }

    public onCountryFilterChanged($event: IFilterSelectItem[]) {
        this.defaultCountryFilter = $event;
        let filterData = this.samplesService.getFilterData(this.defaultProtocolFilter, $event, this.defaultRegionFilter, this.defaultProjectFilter, [], this.dataList);
        this.setFilterData(filterData, { protocol: true, region: true, project: true, country: true });
        this.protocolSponsorPairs = this.samplesService.getProtocolFilterPairs(filterData);
        this.loadSampleCountData();
    }

    public onRegionFilterChanged($event: IFilterSelectItem[]) {
        this.defaultRegionFilter = $event;
        let filterData = this.samplesService.getFilterData(this.defaultProtocolFilter, [], $event, [], [], this.dataList);
        this.setFilterData(filterData, { protocol: true, region: true });
        this.protocolSponsorPairs = this.samplesService.getProtocolFilterPairs(filterData);
        this.loadSampleCountData();
    }

    public onProjectFilterChanged($event: IFilterSelectItem[]) {
        this.defaultProjectFilter = $event;
        let filterData = this.samplesService.getFilterData(this.defaultProtocolFilter, [], this.defaultRegionFilter, $event, [], this.dataList);
        this.setFilterData(filterData, { protocol: true, region: true, project: true });
        this.protocolSponsorPairs = this.samplesService.getProtocolFilterPairs(filterData);
        this.loadSampleCountData();
    }

    public onProtocolFilterChanged($event: IFilterSelectItem[]) {
        this.defaultProtocolFilter = $event;
        let filterData = this.samplesService.getDropdownFilterData(this.dataList, $event, 'protocolCode');
        this.setFilterData(filterData, { protocol: true });
        this.protocolSponsorPairs = this.samplesService.getProtocolFilterPairs(filterData);
        this.loadSampleCountData();
    }

    public onInvestigatorNameFilterChanged($event: IFilterSelectItem[]) {
        this.defaultInvestigatorFilter = $event;
        let filterData = this.samplesService.getFilterData(
            this.defaultProtocolFilter, this.defaultCountryFilter, this.defaultRegionFilter,
            this.defaultProjectFilter, $event, this.dataList);
        this.setFilterData(filterData, { protocol: true, region: true, project: true, country: true, investigator: true });
        this.protocolSponsorPairs = this.samplesService.getProtocolFilterPairs(filterData);
        this.loadSampleCountData();
    }

    public onCollapseButton() {
        this.manualDrawer = true;
        this.collapseDrawer = !this.collapseDrawer;
    }

    public getProtocolList() {
        return this.selectedStudyGroup$.studyList.map(item => '  ' + item.protocolCode);
    }

    public onMyFilterChange(filter: UserFilter) {
        this.isLoadCountEnabled = false;
        this.clearFilter(false);
        if (filter.params && filter && filter.params.filterType) {
            this.isEnableSaveFilter = true;
            this.selectedFilter = filter;
            let selectedFilters = [];
            if (filter.params.selectedFilters) {
                selectedFilters = JSON.parse(filter.params.selectedFilters);
            }
            const selectedProtocolFilters = selectedFilters.find((sf: any) => sf.label === FilterNames.protocol);
            const selectedProjectFilters = selectedFilters.find((sf: any) => sf.label === FilterNames.project);
            const selectedRegionFilters = selectedFilters.find((sf: any) => sf.label === FilterNames.region);
            const selectedCountryFilters = selectedFilters.find((sf: any) => sf.label === FilterNames.country);
            const selectedInvestigatorNumFilters = selectedFilters.find((sf: any) => sf.label === FilterNames.investigatorNum);
            if (selectedProtocolFilters && selectedProtocolFilters.filterItems) {
                let protocols = this.samplesService.getFilteredItems(this.protocolFilters, selectedProtocolFilters.filterItems);
                if (protocols && protocols.length > 0) {
                    this.onProtocolFilterChanged(protocols);
                }
            }
            if (selectedRegionFilters && selectedRegionFilters.filterItems) {
                let region = this.samplesService.getFilteredItems(this.regions$, selectedRegionFilters.filterItems);
                if (region && region.length > 0) {
                    this.onRegionFilterChanged(region);
                }
            }
            if (selectedProjectFilters && selectedProjectFilters.filterItems) {
                let project = this.samplesService.getFilteredItems(this.projects$, selectedProjectFilters.filterItems);
                if (project && project.length > 0) {
                    this.onProjectFilterChanged(project);
                }
            }
            if (selectedCountryFilters && selectedCountryFilters.filterItems) {
                let countries = this.samplesService.getFilteredItems(this.allCountries$, selectedCountryFilters.filterItems);
                if (countries && countries.length > 0) {
                    this.onCountryFilterChanged(countries);
                }
            }
            if (selectedInvestigatorNumFilters && selectedInvestigatorNumFilters.filterItems) {
                let invsiteList = this.samplesService.getFilteredItems(this.invNumber$, selectedInvestigatorNumFilters.filterItems);
                if (invsiteList && invsiteList.length > 0) {
                    this.onInvestigatorNameFilterChanged(invsiteList);
                }
            }
            this.isLoadCountEnabled = true; 
            this.loadSampleCountData();
            this.changeDetectorRef.markForCheck();
        }
    }

    public onMyFilterSubmit(filter: { action: string, userFilter: UserFilter }) {
        this.isEnableSaveFilter = false;
        if (filter.action === 'delete') {
            this.showMyFilterLoading = true;
            this.filterStorage.delete(this.selectedFilter.id).then(() => {
                this.showMyFilterLoading = false;
                this.selectedFilter = null;
                this.clearFilter();
                this.myFilterItems = this.filterStorage.getFilters(this.filterInfo.groupName, null, null);
                this.changeDetectorRef.markForCheck();
            });
            return;
        }
        if (filter.userFilter) {
            this.isEnableSaveFilter = true;
            if (filter.userFilter.params && !filter.userFilter.params.selectedFilters) {// && this.selectedFilter && this.selectedFilter.params
                this.selectedFilterList = [];
                this.selectedStatus = null;
                this.selectFilterService.clearFilterSelection(true);
                this.defaultProtocolFilter = [];
                this.defaultRegionFilter = [];
                this.defaultProjectFilter = [];
                this.defaultInvestigatorFilter = [];
                this.defaultCountryFilter = [];
            }
            this.selectedFilter = filter.userFilter;
        }
    }

    public async onSaveFilterClick() {
        this.isEnableSaveFilter = false;
        if (this.selectedFilter) {
            const selectedFilters = [];
            if (this.defaultProtocolFilter && this.defaultProtocolFilter.length) {
                selectedFilters.push({ label: FilterNames.protocol, filterItems: this.defaultProtocolFilter });
            }
            if (this.defaultRegionFilter && this.defaultRegionFilter.length) {
                selectedFilters.push({ label: FilterNames.region, filterItems: this.defaultRegionFilter });
            }
            if (this.defaultProjectFilter && this.defaultProjectFilter.length) {
                selectedFilters.push({ label: FilterNames.project, filterItems: this.defaultProjectFilter });
            }
            if (this.defaultCountryFilter && this.defaultCountryFilter.length) {
                selectedFilters.push({ label: FilterNames.country, filterItems: this.defaultCountryFilter });
            }
            if (this.defaultInvestigatorFilter && this.defaultInvestigatorFilter.length) {
                selectedFilters.push({ label: FilterNames.investigatorNum, filterItems: this.defaultInvestigatorFilter });
            }
            this.selectedFilter.group = this.filterInfo.groupName;
            this.selectedFilter.params.filterType = this.previousRequestData.filterType;

            if (selectedFilters && selectedFilters.length) {
                this.selectedFilter.params.selectedFilters = JSON.stringify(selectedFilters);
            }
            if (this.previousRequestData.filterId) {
                this.selectedFilter.params.filterId = JSON.stringify(this.previousRequestData.filterId);
            }
            if (this.previousRequestData.investigatorSiteId) {
                this.selectedFilter.params.investigatorSiteId = JSON.stringify(this.previousRequestData.investigatorSiteId);
            }
            this.showMyFilterLoading = true;
            await this.filterStorage.save(this.selectedFilter).then(() => {
                this.showMyFilterLoading = false;
                this.isEnableSaveFilter = true;
                this.myFilterItems = this.filterStorage.getFilters(this.filterInfo.groupName, null, null);
                this.selectedFilter = this.myFilterItems.find(fi => fi.name === this.selectedFilter.name);
                this.changeDetectorRef.markForCheck();
            });
        }
    }

    private loadProtocolData() {
        return !this.isProtocol ? this.getProtocolFilterList() : [{
            id: this.selectedProtocol$.protocolFk,
            value: this.selectedProtocol$.protocolCode,
            checked: true,
            sponsorName: this.selectedProtocol$.sponsorName
        }];
    }

    private initializePageData(dashboardData: any) {
        let id = 0;
        this.activatedRoute.params.subscribe(param => {
            id = Number(param.studyGroupId);
            if (param.type === 'study-group') {
                this.selectedStudyGroup$ = JSON.parse(dashboardData);
            }
            if (param.type === 'protocol') {
                this.isProtocol = true;
                this.selectedProtocol$ = JSON.parse(dashboardData);
            }
        });
        if ((this.isProtocol && this.selectedProtocol$.protocolFk !== id) ||
            (!this.isProtocol && this.selectedStudyGroup$.groupPk !== id)) {
            this.urlBuilder.esa().sampletracking().home().navigate();
            return;
        }
        let routerUrl: IBreadcrumb = {
            label: this.isProtocol ? 'Protocol ' + this.selectedProtocol$.protocolCode :
                'Study Grouping ' + this.selectedStudyGroup$.groupName,
            url: this.isProtocol ? this.urlBuilder.esa().sampletracking().protocolInfo(this.selectedProtocol$).url() :
                this.urlBuilder.esa().sampletracking().studyGroup(this.selectedStudyGroup$).url(), 
            clearCachedData: true,
            isListUrl: true,
        };
        this.breadCrumbService.AddRouterToBreadCrumb(routerUrl);
    }

    private loadSampleCountData() {
        if (!this.isLoadCountEnabled) { return; }
        let requestData = this.samplesService.getSampleCountRequestData(this.defaultProtocolFilter, this.defaultCountryFilter, this.defaultProjectFilter,
                                                                        this.defaultInvestigatorFilter, this.defaultRegionFilter, this.invNumber$);
        if (!requestData && this.protocolFilters) {
            requestData = this.samplesService.getCountRequestData(this.protocolFilters, FilterTypeEnum.protocol, true, this.invNumber$);
        }
        if (requestData) {
            requestData.sponsorProtocolIds = this.protocolSponsorPairs;
        }
        this.loadCountData(requestData);
    }

    private loadCountData(requestData: SampleTrackingSampleCountRequest) {
        this.storage.getSampleCountData(requestData).subscribe(countData => {
            this.getSampleStatusInfo(countData);
        });
        this.previousRequestData = requestData;
    }

    private getProtocolFilterList() {
        const protocolList: IFilterSelectItem[] = [];
        for (var study of this.selectedStudyGroup$.studyList) {
            protocolList.push({ id: study.protocolPk, value: study.protocolCode, sponsorName: study.sponsorName });
        }
        return protocolList;
    }

    private getProtocolFilterData() {
        const filterRequestData: PortfolioFilterRequestData[] = [];
        for (var protocolItem of this.protocol) {
            filterRequestData.push({
                protocolCode: protocolItem.value,
                sponsorName: protocolItem.sponsorName
            });
        }
        return this.storage.getPortfolioFilterData(filterRequestData);
    }

    private getSampleStatusInfo(sampleData: SampleCountData) {
        this.sampleStatusList = this.samplestatusService.getSampleStatusInfo(sampleData);
        this.selectedStatus = null;
        this.changeDetectorRef.markForCheck();
        this.loadingComplete = true;
    }

    private setFilterData(portfolioData: ProtocolFilterData[], exclude?: ExcludeFilterItems) {
        if (!portfolioData || !portfolioData.length && this.isProtocol) { return null; }
        if (!exclude || !exclude.protocol) {
            this.protocolFilters = this.samplesService.loadDropDownItems(portfolioData, 'protocolId', 'protocolCode');
            this.defaultProtocolFilter = this.samplesService.getFilteredItems(this.protocolFilters, this.defaultProtocolFilter);
            if (this.isProtocol) {
                this.defaultProtocolFilter = this.protocolFilters;
            }
        }
        if (!exclude || !exclude.region) {
            this.regions$ = this.samplesService.loadDropDownItems(portfolioData, null, 'region');
            this.defaultRegionFilter = this.samplesService.getFilteredItems(this.regions$, this.defaultRegionFilter);
            if (this.defaultRegionFilter && this.defaultRegionFilter.length > 0) {
                portfolioData = this.samplesService.getDropdownFilterData(portfolioData, this.defaultRegionFilter, 'region');
            }
        }
        if (!exclude || !exclude.project) {
            this.projects$ = this.samplesService.loadDropDownItems(portfolioData, 'subprotocolId', 'projectNumber');
            this.defaultProjectFilter = this.samplesService.getFilteredItems(this.projects$, this.defaultProjectFilter);
            if (this.defaultProjectFilter && this.defaultProjectFilter.length > 0) {
                portfolioData = this.samplesService.getDropdownFilterData(portfolioData, this.defaultProjectFilter, 'projectNumber');
            }
        }
        if (!exclude || !exclude.country) {
            this.allCountries$ = this.samplesService.loadDropDownItems(portfolioData, 'countryId', 'countryName');
            this.defaultCountryFilter = this.samplesService.getFilteredItems(this.allCountries$, this.defaultCountryFilter);
            if (this.defaultCountryFilter && this.defaultCountryFilter.length > 0) {
                portfolioData = this.samplesService.getDropdownFilterData(portfolioData, this.defaultCountryFilter, 'countryName');
            }
        }
        if (!exclude || !exclude.investigator) {
            this.invNumber$ = this.samplesService.loadDropDownItems(portfolioData, 'investigatorSiteId', 'investigatorNumber', 'investigatorName');
            this.defaultInvestigatorFilter = this.samplesService.getFilteredItems(this.invNumber$, this.defaultInvestigatorFilter);
        }

        this.changeDetectorRef.markForCheck();
        return portfolioData;
    }

    private setListViewData() {
        this.selectedFilterList = this.samplesService.loadSelectedFilterList(this.defaultProtocolFilter, this.defaultRegionFilter, this.defaultProjectFilter,
                                                                             this.defaultCountryFilter, this.defaultInvestigatorFilter);
        const filterInfo: FilterSamplesInfo = {
            filterData: this.dataList ? this.dataList : [],
            selectedProtocol$: this.selectedProtocol$,
            selectedStudyGroup: this.selectedStudyGroup$,
            selectedFilterItems: this.selectedFilterList,
            selectedStatus: this.selectedStatus
        };
        localStorage.setItem('listViewData', JSON.stringify(filterInfo));
        return filterInfo;
    }
}